import type { IntlShape } from 'react-intl';
import { ProductType } from '../../../bff-api/baskets/model/ProductType';
import { BloomreachProductType } from '../hooks/use-search';
import type { Product } from '../../../bff-api/products/model/Product';
import type { DropdownItems } from '../../../components/dropdown/DropdownItem';
import type { SearchProduct } from '../hooks/use-search/use-search-types';

export const getSubTitle = (
    intl: IntlShape,
    types: BloomreachProductType[],
    total: number,
): string => {
    if (types.length > 2) {
        return intl.formatMessage(
            {
                id: 'found.items.in.products.classes.recipes',
                defaultMessage: 'Found {total} items in products, classes & recipes',
            },
            { total },
        );
    }

    if (types.length === 2) {
        if (!types.includes(BloomreachProductType.recipe)) {
            return intl.formatMessage(
                {
                    id: 'found.items.in.products.classes',
                    defaultMessage: 'Found {total} items in products & classes',
                },
                { total },
            );
        }

        if (!types.includes(BloomreachProductType.culinary)) {
            return intl.formatMessage(
                {
                    id: 'found.items.in.products.recipes',
                    defaultMessage: 'Found {total} items in products & recipes',
                },
                { total },
            );
        }

        return intl.formatMessage(
            {
                id: 'found.items.in.classes.recipes',
                defaultMessage: 'Found {total} items in classes & recipes',
            },
            { total },
        );
    }

    if (types.includes(BloomreachProductType.hardgood)) {
        return intl.formatMessage(
            { id: 'found.items.in.products', defaultMessage: 'Found {total} items in products' },
            { total },
        );
    }

    if (types.includes(BloomreachProductType.culinary)) {
        return intl.formatMessage(
            { id: 'found.items.in.classes', defaultMessage: 'Found {total} items in classes' },
            { total },
        );
    }

    return intl.formatMessage(
        { id: 'found.items.in.recipes', defaultMessage: 'Found {total} items in recipes' },
        { total },
    );
};

export const getSearchSortItems = (
    intl: IntlShape,
    productType: BloomreachProductType,
): DropdownItems => {
    if (productType === BloomreachProductType.recipe) {
        return [
            {
                id: 'relevance',
                value: '',
                label: intl.formatMessage({
                    id: 'sort.by.relevance',
                    defaultMessage: 'Relevance',
                }),
            },
            {
                id: 'title-asc',
                value: 'title-asc',
                label: intl.formatMessage({
                    id: 'sort.by.title',
                    defaultMessage: 'Name: A to Z',
                }),
            },
            {
                id: 'title-desc',
                value: 'title-desc',
                label: intl.formatMessage({
                    id: 'sort.by.title.desc',
                    defaultMessage: 'Name: Z to A',
                }),
            },
            {
                id: 'brand-asc',
                value: 'brand-asc',
                label: intl.formatMessage({
                    id: 'sort.by.author.asc',
                    defaultMessage: 'Author: A to Z',
                }),
            },
            {
                id: 'brand-desc',
                value: 'brand-desc',
                label: intl.formatMessage({
                    id: 'sort.by.author.desc',
                    defaultMessage: 'Author: Z to A',
                }),
            },
        ];
    }

    return [
        {
            id: 'relevance',
            value: '',
            label: intl.formatMessage({
                id: 'sort.by.relevance',
                defaultMessage: 'Relevance',
            }),
        },
        {
            id: 'price-asc',
            value: 'price-asc',
            label: intl.formatMessage({
                id: 'sort.by.price.low.to.high',
                defaultMessage: 'Price: Low to High',
            }),
        },
        {
            id: 'price-desc',
            value: 'price-desc',
            label: intl.formatMessage({
                id: 'sort.by.price.high.to.low',
                defaultMessage: 'Price: High to Low',
            }),
        },
        {
            id: 'top-rated',
            value: 'top-rated',
            label: intl.formatMessage({
                id: 'sort.by.top.rated',
                defaultMessage: 'Top Ratings',
            }),
        },
        {
            id: 'best-sellers',
            value: 'best-sellers',
            label: intl.formatMessage({
                id: 'most.popular',
                defaultMessage: 'Most Popular',
            }),
        },
    ];
};

export const getProductTileId = (productId: string): string => `product-tile-${productId}`;

/**
 * Maps a SearchProduct from search results to a Product model
 * SearchProduct is a subset of Product with some different property names
 */
export const mapSearchProductToProduct = (searchProduct: SearchProduct): Product => {
    const prices =
        'searchPrices' in searchProduct && searchProduct.searchPrices
            ? {
                  regularPrice: searchProduct.searchPrices.regularPrice?.[0],
                  salePrice: searchProduct.searchPrices.salePrice?.[0],
                  suggestedPrice: searchProduct.searchPrices.suggestedPrice?.[0],
              }
            : undefined;

    const customVariants = searchProduct.c_variants?.map((variant) => ({
        id: variant.id,
        colorName: variant.colorName || '',
        swatchColor: variant.swatchColor || null,
        c_imageUrl: variant.c_imageUrl,
        isOutOfStock: false,
    }));

    let c_productType: ProductType | undefined;

    if (searchProduct.productType === BloomreachProductType.hardgood) {
        c_productType = ProductType.HARD_GOOD;
    }
    if (searchProduct.productType === BloomreachProductType.culinary) {
        c_productType = ProductType.CULINARY;
    }

    return {
        id: searchProduct.id,
        name: searchProduct.name,
        brand: searchProduct.brand,
        longDescription: searchProduct.longDescription,
        c_pwaUrl: searchProduct.c_pwaUrl,
        c_imageUrl: searchProduct.c_imageUrl,
        c_plpImageUrl: searchProduct.c_imageUrl,
        prices,
        price: prices?.salePrice || prices?.regularPrice,
        c_badge: searchProduct.c_badge,
        c_turntoAverageRating: searchProduct.c_turntoAverageRating,
        c_turntoTotalReviewCount: searchProduct.c_turntoTotalReviewCount,
        c_productType,
        c_variants: customVariants,
        c_shippingOptions: [],
        variants: [],
        master: searchProduct.master,
    };
};
